import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Card,
  Button,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Space,
  Dropdown,
  App,
  Avatar,
  Typography,
  Tooltip,
  Modal,
  Form,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  UserOutlined,
  MoreOutlined,
  KeyOutlined,
  PoweroffOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  StopOutlined,
  LockOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { debounce } from 'lodash';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

// --- Definisikan tipe data untuk state ---
interface Role {
  id: string;
  name: string;
  display_name: string;
}

interface PropertyArea {
  id: string;
  name: string;
}

interface UserPropertyArea {
  property_area_id: string;
  property_area?: {
    name: string;
  };
}

interface User {
  id: string;
  full_name: string;
  username: string;
  email: string;
  photo_url?: string;
  role: Role;
  role_id: string;
  user_property_areas: UserPropertyArea[];
  phone: string;
  is_active: boolean;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
}

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [propertyAreas, setPropertyAreas] = useState<PropertyArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    role_id: '',
    status: '',
    search: '',
    property_area_id: '',
  });
  const [isAllAreas, setIsAllAreas] = useState(false);
  const { message } = App.useApp();
  const { user: contextUser, hasPermission } = useAuth();

  const canCreate = hasPermission('user.create');
  const canUpdate = hasPermission('user.update');
  const canDelete = hasPermission('user.delete');

  const { current, pageSize } = pagination;

  const handleAllAreasChange = (checked: boolean) => {
    setIsAllAreas(checked);
    if (checked) {
      const allAreaIds = propertyAreas.map(area => area.id);
      form.setFieldsValue({ property_area_id: allAreaIds });
    } else {
      form.setFieldsValue({ property_area_id: [] });
    }
  };

  const fetchUsers = useCallback(async (currentFilters: typeof filters, currentPage: number, currentPageSize: number) => {
    setLoading(true);
    try {
      // Terjemahkan filter status frontend ke parameter backend
      const { status, ...otherFilters } = currentFilters;
      const params: { [key: string]: any } = {
        page: currentPage,
        limit: currentPageSize,
        ...otherFilters,
      };

      if (status === 'active') {
        params.is_active = 'true';
      } else if (status === 'inactive') {
        params.is_active = 'false';
      } else if (status === 'locked') {
        params.is_locked = 'true';
      }

      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const response = await api.get<{ data: User[]; meta: any }>('users', { params });
      let { data, meta } = response.data;

      // **SOLUSI DEFINITIF:** Sinkronisasi dengan AuthContext SEBELUM set state
      if (contextUser && data) {
        data = data.map(u => {
          if (u.id === contextUser.id) {
            const contextUpdatedAt = contextUser.updated_at ? new Date(contextUser.updated_at).getTime() : 0;
            const apiUpdatedAt = u.updated_at ? new Date(u.updated_at).getTime() : 0;

            // Jika data context lebih baru, timpa photo_url dari API
            if (contextUpdatedAt > apiUpdatedAt) {
              return { ...u, photo_url: contextUser.photo_url };
            }
          }
          return u;
        });
      }

      setUsers(data || []);
      setPagination(prev => ({
        ...prev,
        total: meta?.total || data?.length || 0,
        current: currentPage,
        pageSize: currentPageSize,
      }));
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      message.error('Gagal memuat data user');
    } finally {
      setLoading(false);
    }
  }, [message, contextUser]); // Tambahkan contextUser sebagai dependency

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [rolesRes, areasRes] = await Promise.all([
          api.get<{ data: Role[] }>('roles'),
          api.get<{ data: PropertyArea[] }>('property-areas/role', { params: { limit: 1000, is_active: 'true' } })
        ]);
        setRoles(rolesRes.data.data || []);
        setPropertyAreas(areasRes.data.data || []);
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
        message.error('Gagal memuat data pendukung (role/property area)');
      }
    };

    fetchInitialData();
  }, [message]);

  useEffect(() => {
    fetchUsers(filters, current, pageSize);
  }, [filters, current, pageSize, fetchUsers]);


  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
      }, 500),
    []
  );

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleToggleActive = (user: User) => {
    Modal.confirm({
      title: `Konfirmasi ${user.is_active ? 'Nonaktifkan' : 'Aktifkan'} User`,
      content: `Apakah Anda yakin ingin ${user.is_active ? 'menonaktifkan' : 'mengaktifkan'} user ${user.full_name}?`,
      onOk: async () => {
        try {
          const payload = {
            username: user.username,
            email: user.email,
            full_name: user.full_name,
            phone: user.phone,
            role_id: user.role_id,
            is_active: !user.is_active,
            property_area_id: user.user_property_areas?.map(upa => upa.property_area_id) || [],
          };
          await api.put(`users/${user.id}`, payload);
          message.success(`User berhasil di${user.is_active ? 'nonaktifkan' : 'aktifkan'}`);
          fetchUsers(filters, current, pageSize);
        } catch (error: any) {
          console.error('Failed to toggle user status:', error);
          message.error(error.response?.data?.message || 'Gagal memperbarui status user');
        }
      },
      okText: 'Ya',
      cancelText: 'Batal',
      okButtonProps: { danger: user.is_active },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const submitValues = {
        ...values,
        property_area_id: values.property_area_id || [],
        is_all_property_areas: values.all_areas || false,
      };
      delete submitValues.all_areas;

      if (editingUser) {
        submitValues.is_active = editingUser.is_active;
        await api.put(`users/${editingUser.id}`, submitValues);
        message.success('Pengguna berhasil diperbarui');
      } else {
        submitValues.is_active = values.is_active !== undefined ? values.is_active : true;
        // Password tidak dikirim, backend akan set default
        delete submitValues.password;
        await api.post('users', submitValues);

        Modal.success({
          title: 'Pengguna Berhasil Dibuat',
          content: (
            <div>
              <p>Pengguna baru telah berhasil ditambahkan ke dalam sistem.</p>
              <p>Password default untuk login pertama kali adalah: <strong>leads123</strong></p>
              <br />
              <p>Harap informasikan kepada user untuk segera mengubah password setelah login.</p>
            </div>
          ),
          okText: 'Mengerti',
        });
      }

      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers(filters, current, pageSize);
    } catch (error: any) {
      console.error('Failed to save user:', error);
      message.error(error.response?.data?.message || 'Gagal menyimpan user');
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleEdit = async (user: User) => {
    setEditingUser(user);
    setModalVisible(true);
    form.resetFields();
    setLoading(true);

    try {
      const response = await api.get<{ data: User }>(`users/${user.id}`);
      const freshUser = response.data.data;

      const propertyAreaIds = freshUser.user_property_areas?.map(upa => upa.property_area_id) || [];
      const allAreaIds = propertyAreas.map(area => area.id);
      const allSelected = allAreaIds.length > 0 && propertyAreaIds.length === allAreaIds.length;
      setIsAllAreas(allSelected);

      form.setFieldsValue({
        username: freshUser.username,
        email: freshUser.email,
        full_name: freshUser.full_name,
        phone: freshUser.phone,
        role_id: freshUser.role_id,
        is_active: freshUser.is_active,
        property_area_id: propertyAreaIds,
        all_areas: allSelected,
      });
    } catch (error) {
      message.error('Gagal memuat data user untuk diedit.');
      setModalVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setIsAllAreas(false);
    setModalVisible(true);
  };

  const handleResetPassword = (userId: string) => {
    Modal.confirm({
      title: 'Reset Password',
      content: 'Apakah Anda yakin ingin mereset password user ini? Password akan diatur ulang ke default.',
      onOk: async () => {
        try {
          await api.post(`users/${userId}/reset-password`);
          Modal.success({
            title: 'Password Berhasil Direset',
            content: (
              <div>
                <p>Password untuk user ini telah berhasil diatur ulang.</p>
                <p>Password baru yang digunakan adalah: <strong>leads123</strong></p>
                <br />
                <p>Harap informasikan kepada user terkait perubahan ini.</p>
              </div>
            ),
            okText: 'Mengerti',
          });
        } catch (error: any) {
          console.error('Failed to reset password:', error);
          message.error('Gagal mereset password');
        }
      },
      okText: 'Ya, Reset',
      cancelText: 'Batal',
    });
  };

  const actionMenuItems = (record: User) => {
    const items: any[] = [];
    if (canUpdate) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Edit',
        onClick: () => handleEdit(record),
      });
      items.push({
        key: 'reset-password',
        icon: <KeyOutlined />,
        label: 'Reset Password',
        onClick: () => handleResetPassword(record.id),
      });
    }
    if (canDelete) {
      if (items.length > 0) {
        items.push({ type: 'divider' });
      }
      items.push({
        key: 'toggle-active',
        icon: <PoweroffOutlined />,
        label: record.is_active ? 'Nonaktifkan' : 'Aktifkan',
        onClick: () => handleToggleActive(record),
        danger: record.is_active,
      });
    }
    return items;
  };

  const columns: any[] = [
    {
      title: 'User',
      key: 'user_info',
      width: 280,
      render: (_: any, record: User) => (
        <Space align="center">
          <Avatar size={48} icon={<UserOutlined />} src={record.photo_url} />
          <div>
            <Text strong style={{ display: 'block' }}>{record.full_name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: ['role', 'display_name'],
      key: 'role',
      width: 200,
      render: (text: string, record: User) => {
        const roleName = text || record.role?.name;
        return (
          <Tooltip title={roleName}>
            <Tag color="blue">{roleName}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Property Area',
      key: 'property_area',
      width: 250,
      render: (_: any, record: User) => {
        const areas = record.user_property_areas || [];
        if (areas.length === 0) return <Text type="secondary">-</Text>;

        const MAX_VISIBLE = 2;
        const visibleAreas = areas.slice(0, MAX_VISIBLE);
        const hiddenCount = areas.length - MAX_VISIBLE;

        const content = (
          <Space direction="vertical" size="small">
            {visibleAreas.map(upa => (
              <Tag key={upa.property_area_id} color="geekblue">
                {upa.property_area?.name || 'Area tidak valid'}
              </Tag>
            ))}
            {hiddenCount > 0 && (
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                +{hiddenCount} lagi
              </Text>
            )}
          </Space>
        );

        if (areas.length > MAX_VISIBLE) {
          return (
            <Tooltip
              title={
                <Space direction="vertical" align="start" size={1}>
                  {areas.map(upa => (
                    <span key={upa.property_area_id}>- {upa.property_area?.name || 'N/A'}</span>
                  ))}
                </Space>
              }
            >
              {content}
            </Tooltip>
          );
        }
        return content;
      },
    },
    {
      title: 'No. Telepon',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (phone: string) => phone || <Text type="secondary">-</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_: any, record: User) => {
        const statusTag = record.is_active ? (
          <Tag
            color="success"
            icon={<CheckCircleOutlined />}
            style={{ margin: 0 }}
          >
            Aktif
          </Tag>
        ) : (
          <Tag
            icon={<StopOutlined />}
            style={{ margin: 0 }}
          >
            Nonaktif
          </Tag>
        );

        if (record.is_locked) {
          return (
            <Space>
              {statusTag}
              <Tooltip title="Akun ini dikunci">
                <LockOutlined style={{ color: 'red' }} />
              </Tooltip>
            </Space>
          );
        }

        return statusTag;
      },
    },
    {
      title: 'Tanggal Dibuat',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 80,
      align: 'center',
      render: (_: any, record: User) => {
        const menuItems = actionMenuItems(record);
        if (menuItems.length === 0) return null;
        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="top" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Manajemen User
          </Title>
          <Paragraph type="secondary">
            Daftar semua user yang terdaftar dalam sistem.
          </Paragraph>
        </Col>
        {canCreate && (
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Tambah User
            </Button>
          </Col>
        )}
      </Row>

      <Card bordered={false}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Cari nama atau email..."
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              onChange={(e) => debouncedSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter berdasarkan role"
              style={{ width: '100%' }}
              allowClear
              value={filters.role_id || undefined}
              onChange={(value) => handleFilterChange('role_id', value)}
            >
              {roles.map(role => (
                <Option key={role.id} value={role.id}>
                  {role.display_name || role.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter Property Area"
              style={{ width: '100%' }}
              allowClear
              value={filters.property_area_id || undefined}
              onChange={(value) => handleFilterChange('property_area_id', value)}
            >
              {propertyAreas.map(area => (
                <Option key={area.id} value={area.id}>
                  {area.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter berdasarkan status"
              style={{ width: '100%' }}
              allowClear
              value={filters.status || undefined}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="active">Aktif</Option>
              <Option value="inactive">Nonaktif</Option>
              <Option value="locked">Locked</Option>
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} user`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingUser ? 'Edit User' : 'Tambah User Baru'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ is_active: true }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Username"
                name="username"
                rules={[{ required: true, message: 'Username wajib diisi' }]}
              >
                <Input placeholder="Masukkan username" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Email wajib diisi' },
                  { type: 'email', message: 'Format email tidak valid' },
                ]}
              >
                <Input placeholder="Masukkan email" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Nama Lengkap"
                name="full_name"
                rules={[{ required: true, message: 'Nama lengkap wajib diisi' }]}
              >
                <Input placeholder="Masukkan nama lengkap" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="No. Telepon" name="phone">
                <Input addonBefore="+62" placeholder="Masukkan nomor telepon" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Role"
                name="role_id"
                rules={[{ required: true, message: 'Role wajib dipilih' }]}
              >
                <Select placeholder="Pilih role">
                  {roles.map(role => (
                    <Option key={role.id} value={role.id}>
                      {role.display_name || role.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Semua Property Area" name="all_areas" valuePropName="checked">
                <Switch onChange={handleAllAreasChange} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Property Area"
                name="property_area_id"
                rules={[{ required: true, message: 'Pilih minimal 1 property area' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Pilih satu atau lebih property area"
                  allowClear
                  disabled={isAllAreas}
                >
                  {propertyAreas.map(area => (
                    <Option key={area.id} value={area.id}>
                      {area.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersList;